import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { LayoutDashboard, FileText, Users, LogOut, Menu, X, Shield, Flag, MessageSquare } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const AdminLayout = ({ children }) => {
  const { user, role, logout, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingReportsCount, setPendingReportsCount] = useState(0);

  // Charger le nombre de signalements en attente
  useEffect(() => {
    const loadReportsCount = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'reports'), where('status', '==', 'pending'))
        );
        setPendingReportsCount(snap.size);
      } catch (e) {}
    };
    if (user && role === 'admin') loadReportsCount();
  }, [user, role]);

  if (loading) return null;
  if (!user || role !== 'admin') return null;

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Déconnexion réussie');
      navigate('/auth');
    } catch {
      toast.error('Erreur lors de la déconnexion');
    }
  };

  const adminNavigation = [
    {
      name: 'Dashboard',
      path: '/admin/dashboard',
      icon: LayoutDashboard,
      badge: pendingReportsCount > 0 ? pendingReportsCount : null,
    },
    { name: 'Articles', path: '/admin/articles', icon: FileText },
    { name: 'Messages', path: '/admin/messages', icon: MessageSquare },
    { name: 'Utilisateurs', path: '/admin/users', icon: Users },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-950 dark:to-gray-900">
      {/* Top Navbar */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-red-600 to-orange-600 shadow-lg">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 hover:bg-red-700 rounded-lg"
            >
              {sidebarOpen ? (
                <X className="w-5 h-5 text-white" />
              ) : (
                <Menu className="w-5 h-5 text-white" />
              )}
            </button>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-white" />
              <span className="text-xl font-bold text-white">Remadia Admin</span>
            </div>
            {/* Badge signalements dans la navbar */}
            {pendingReportsCount > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1 bg-white/20 rounded-full">
                <Flag className="w-4 h-4 text-white" />
                <span className="text-white text-sm font-bold">{pendingReportsCount}</span>
                <span className="text-white/80 text-xs">
                  signalement{pendingReportsCount > 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2">
                <Avatar>
                  <AvatarImage />
                  <AvatarFallback className="bg-white text-red-600 font-semibold">
                    {user?.email?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user.email}</p>
                <p className="text-xs text-muted">Administrateur</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Dashboard utilisateur
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 min-h-screen ${sidebarOpen ? 'block' : 'hidden'} lg:block`}
        >
          <nav className="p-4 space-y-1">
            {adminNavigation.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? 'bg-red-500 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center gap-3">
                  <item.icon className="w-5 h-5" />
                  {item.name}
                </div>
                {item.badge && (
                  <span
                    className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      location.pathname === item.path
                        ? 'bg-white text-red-500'
                        : 'bg-red-500 text-white'
                    }`}
                  >
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 p-6">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
